// frontend/src/components/reputation/CitationsFixPacket.jsx
// Version: 1.0 (задача 260918-0i5, SOGLASOVANO.md от 2026-09-18)
// Description: Пакет правок по площадкам — заход за заходом, а не плоский список.
//
// 🔴 ПРАВИЛО НОЛЬ. Здесь нет и не может быть ни одного значения фирмы: ни адреса,
//    ни телефона, ни номера Bar, ни ссылки на каталог. ВСЁ приходит пропсами из
//    ответа ручки `/api/v1/reputation/citations/summary`, где значения читаются
//    из канона в момент запроса. Литерал здесь завёл бы вторую копию канона —
//    ровно то, что случилось 16.09.2026 и разошлось в первой же строке.
//
// 🔴 ЭТОТ КОМПОНЕНТ НИЧЕГО НЕ ЧИНИТ. Ручек записи к чужим площадкам нет ни к
//    одной; всё, что он делает, — готовит человеку заход и снимает с него
//    пересборку значений по крупицам. `axios` здесь не импортируется намеренно.
//
// 🔴 РОД РАБОТЫ РАЗЛИЧАЕТСЯ УСТРОЙСТВОМ КАРТОЧКИ, А НЕ ПОДПИСЬЮ. Кнопка
//    «скопировать» — это утверждение, что выбор уже сделан. Поместив рядом
//    принятое решение и непринятое, интерфейс сообщит о втором то же, что о
//    первом, и мелкий шрифт этого не отменит. Поэтому у немеханических родов
//    узел кнопки НЕ СОЗДАЁТСЯ — не `disabled` и не серый цвет, а отсутствие.

import React, { useState } from 'react';
import { ClipboardCopy, AlertTriangle, HelpCircle, Trash2, Clock } from 'lucide-react';
import { opisatZamechanie } from './CitationsWorklist';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';

// Роды считает ДЕРЖАТЕЛЬ КАНОНА на сервере (`fix_targets`). Здесь только имена,
// по которым род узнаётся, — своей классификации фронт не заводит.
const ROD_MECHANICAL = 'mechanical';
const ROD_REMOVE = 'remove';
const ROD_DECISION = 'decision';
const ROD_AWAIT = 'await';
const ROD_NOT_OBSERVED = 'not_observed';

const getPolya = (t) => ({
  name: t('rep.modal.packet.fields.name'),
  phone: t('rep.modal.packet.fields.phone'),
  street: t('rep.modal.packet.fields.street'),
  suite: t('rep.modal.packet.fields.suite'),
  city_state_zip: t('rep.modal.packet.fields.city_state_zip'),
  website: t('rep.modal.packet.fields.website'),
});

const getRoli = (t) => ({
  MAIN: t('rep.modal.packet.roles.MAIN'),
  BRANCH: t('rep.modal.packet.roles.BRANCH'),
});

// Подписи вариантов маршрутного решения — по SOGLASOVANO.md §«Решение владельца».
// 🔴 Ни адреса, ни числа отзывов здесь нет: адрес берётся из `canon.locations`
// по роли варианта в момент показа, а чисел, которых нет в ответе, не бывает.
const getVarianty = (t) => ({
  // 🔴 Развилка «на площадке улицы нет вовсе». Решение 16.09 о публичности
  // улицы обосновано карточкой Google и о текстовых каталогах не говорит
  // ничего, поэтому первая публикация улицы там — вопрос к человеку, а не
  // механическая правка написания.
  city_only: {
    zagolovok: t('rep.modal.packet.variants.city_only.title'),
    posledstvie: t('rep.modal.packet.variants.city_only.desc'),
  },
  street_parity: {
    zagolovok: t('rep.modal.packet.variants.street_parity.title'),
    posledstvie: t('rep.modal.packet.variants.street_parity.desc'),
  },
  keep_branch: {
    zagolovok: t('rep.modal.packet.variants.keep_branch.title'),
    posledstvie: t('rep.modal.packet.variants.keep_branch.desc'),
  },
  move_main: {
    zagolovok: t('rep.modal.packet.variants.move_main.title'),
    posledstvie: t('rep.modal.packet.variants.move_main.desc'),
  },
});

// Вопрос развилки узнаётся по СОСТАВУ вариантов, а не выводится из кода
// замечания: один и тот же код `street_mismatch` несёт два разных предписания,
// и различает их держатель на сервере — здесь только подпись к его решению.
const getRazvilki = (t) => ({
  'keep_branch|move_main': t('rep.modal.packet.razvilki.single_location'),
  'city_only|street_parity': t('rep.modal.packet.razvilki.street_absent'),
});

const voprosRazvilki = (opcii, t) => getRazvilki(t)[(opcii || []).map((o) => o.id).join('|')] || null;

/** Предписания площадки без границ наблюдения: «на снимке не видно» — не работа. */
const predpisaniyaPloshchadki = (dir) =>
  (dir.predpisaniya || []).filter((p) => p.rod !== ROD_NOT_OBSERVED);

const vPakete = (p) => p.rod !== ROD_AWAIT;

/**
 * Сведения о локации для варианта решения.
 *
 * 🔴 Улица показывается ТОЛЬКО при `street_visible`. У локации со скрытой улицей
 * это адрес жилого помещения, и показать его как готовое к переносу значение
 * значило бы предложить опубликовать жильё. Видимость читается ИЗ ОТВЕТА, а не
 * выводится из роли: политика, выведенная из роли, — политика, зашитая в код.
 */
const svedeniyaLokacii = (canon, role) => {
  const loc = (canon?.locations || []).find((l) => l.role === role);
  if (!loc) return { est: false };
  return {
    est: true,
    metka: loc.label,
    adres: loc.street_visible ? loc.address : null,
    skryta: !loc.street_visible,
  };
};

const CitationsFixPacket = ({ canon, directories = [] }) => {
  const { lang: rawLang } = useUiLanguage();
  const lang = rawLang || 'ru';
  const t = (k, v) => perevesti(lang, k, v);

  const POLYA = getPolya(t);
  const ROLI = getRoli(t);
  const VARIANTY = getVarianty(t);

  const [sostoyanieKopii, setSostoyanieKopii] = useState({});

  // 🔴 Никаких сетевых вызовов. Кнопка копирования обязана УМЕТЬ ОТКАЗАТЬ:
  // `navigator.clipboard` есть не в каждой среде (в jsdom его нет вовсе), а
  // молчаливый успех при неудавшемся копировании — обещание, которое никто не
  // проверит, и человек уйдёт вставлять пустоту.
  const kopirovat = async (znachenie, kljuch) => {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        throw new Error(t('rep.modal.packet.clipboard_unavailable'));
      }
      await navigator.clipboard.writeText(znachenie);
      setSostoyanieKopii((s) => ({ ...s, [kljuch]: t('rep.modal.packet.btn_copied') }));
    } catch (e) {
      setSostoyanieKopii((s) => ({
        ...s,
        [kljuch]: t('rep.modal.packet.btn_copy_err', { err: e?.message || 'browser error' }),
      }));
    }
  };

  // ── Арифметика: считается ПО ОТВЕТУ, ни одно число не вписано ──────────────
  const ploshchadki = directories
    .map((d) => ({ dir: d, punkty: predpisaniyaPloshchadki(d) }))
    .filter((x) => x.punkty.some(vPakete));
  const vnePaketa = directories
    .map((d) => ({ dir: d, punkty: predpisaniyaPloshchadki(d).filter((p) => !vPakete(p)) }))
    .filter((x) => x.punkty.length > 0);

  const vsegoPredpisanij = directories.reduce(
    (n, d) => n + predpisaniyaPloshchadki(d).length, 0);
  const vPaketePredpisanij = ploshchadki.reduce(
    (n, x) => n + x.punkty.filter(vPakete).length, 0);
  const vnePaketaPredpisanij = vnePaketa.reduce((n, x) => n + x.punkty.length, 0);
  const trebuyutResheniya = ploshchadki.reduce(
    (n, x) => n + x.punkty.filter((p) => p.rod === ROD_DECISION).length, 0);
  const bezZnacheniya = ploshchadki.reduce(
    (n, x) => n + x.punkty.filter((p) => p.rod === 'unknown').length, 0);

  if (vsegoPredpisanij === 0) {
    return (
      <div className="border border-[#E5E1D6] rounded-2xl p-5 text-xs text-[#687077]">
        {t('rep.modal.packet.empty')}
      </div>
    );
  }

  return (
    <div className="border border-[#E5E1D6] rounded-2xl p-5 space-y-5 bg-[#FCFBF8]">
      {/* ── Шапка: числа из ответа ──────────────────────────────────────────── */}
      <div>
        <h4 className="text-base font-serif text-[#20231F]">
          {t('rep.modal.packet.title')}
        </h4>
        <p className="text-xs text-[#687077] mt-1">
          {t('rep.modal.packet.stats', {
            ploshchadki: ploshchadki.length,
            vPakete: vPaketePredpisanij,
            vnePaketa: vnePaketaPredpisanij,
            trebuyutResheniya,
          })}
          {bezZnacheniya > 0 && t('rep.modal.packet.canon_no_value', { count: bezZnacheniya })}
        </p>
        <p className="text-[11px] text-[#8C7A5B] mt-1 leading-tight">
          {t('rep.modal.packet.no_write_disclaimer')}
        </p>
        <p className="text-[11px] text-[#8C7A5B] mt-1 leading-tight">
          {t('rep.modal.packet.ordering_disclaimer')}
        </p>
      </div>

      {/* ── Заходы по площадкам ─────────────────────────────────────────────── */}
      {ploshchadki.map(({ dir, punkty }) => (
        <div key={dir.key} className="bg-white border border-[#E5E1D6] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-sm text-[#20231F]">{dir.name}</span>
            <span className="text-[11px] text-[#687077]">
              {t('rep.modal.packet.prescriptions_count', { count: punkty.filter(vPakete).length })}
            </span>
          </div>

          {punkty.filter(vPakete).map((p, idx) => {
            const kljuch = `${dir.key}:${idx}`;

            // ── Механическая правка: значение есть, кнопка одна ──────────────
            if (p.rod === ROD_MECHANICAL) {
              return (
                <div key={kljuch} className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-3 space-y-1.5">
                  <div className="text-[11px] text-emerald-900 font-medium">
                    {opisatZamechanie(p.issue, t)}
                  </div>
                  <div className="text-[10px] text-[#687077]">
                    {t('rep.modal.packet.field', { field: POLYA[p.target_field] || p.target_field })}
                    {t('rep.modal.packet.source', { role: ROLI[p.target_role] || p.target_role })}
                  </div>
                  <div className="font-mono text-xs text-[#20231F] bg-white border border-emerald-200 rounded px-2 py-1 break-all">
                    {p.target_value}
                  </div>
                  <button
                    type="button"
                    onClick={() => kopirovat(p.target_value, kljuch)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer"
                  >
                    <ClipboardCopy className="w-3 h-3" />
                    {t('rep.modal.packet.btn_copy')}
                  </button>
                  {sostoyanieKopii[kljuch] && (
                    <div className="text-[10px] text-[#687077]">{sostoyanieKopii[kljuch]}</div>
                  )}
                </div>
              );
            }

            // ── Убрать: значения для вставки НЕТ, кнопки нет как элемента ────
            if (p.rod === ROD_REMOVE) {
              return (
                <div key={kljuch} className="border border-rose-200 bg-rose-50/60 rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-rose-900 font-medium">
                    <Trash2 className="w-3 h-3" />
                    {t('rep.modal.packet.remove_title')}
                  </div>
                  <div className="text-[11px] text-rose-800">{opisatZamechanie(p.issue, t)}</div>
                  <div className="text-[10px] text-rose-700">
                    {t('rep.modal.packet.remove_disclaimer')}
                  </div>
                </div>
              );
            }

            // ── Решение человека: вопрос, варианты, цена каждого ─────────────
            if (p.rod === ROD_DECISION) {
              return (
                <div key={kljuch} className="border border-amber-300 bg-amber-50/70 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-amber-900 font-bold">
                    <AlertTriangle className="w-3 h-3" />
                    {t('rep.modal.packet.decision_title')}
                  </div>
                  <div className="text-[11px] text-amber-900">
                    {voprosRazvilki(p.options, t) || opisatZamechanie(p.issue, t)}
                  </div>

                  {(p.options || []).length > 0 ? (
                    <div className="space-y-1.5">
                      {(p.options || []).map((o) => {
                        const svedeniya = svedeniyaLokacii(canon, o.role);
                        const podpis = VARIANTY[o.id];
                        return (
                          <div key={o.id} className="bg-white border border-amber-200 rounded px-2 py-1.5">
                            <div className="text-[11px] font-medium text-[#20231F]">
                              {podpis ? podpis.zagolovok : o.id}
                            </div>
                            {o.target_value ? (
                              <div className="text-[10px] text-[#687077]">
                                {POLYA[o.target_field] || o.target_field}:{' '}
                                <span className="text-[#20231F]">{o.target_value}</span>
                              </div>
                            ) : (
                              <div className="text-[10px] text-[#687077]">
                                {ROLI[o.role] || o.role}
                                {svedeniya.est && <>{' — '}{svedeniya.metka}</>}
                                {svedeniya.adres && <>{': '}{svedeniya.adres}</>}
                                {svedeniya.skryta && t('rep.modal.packet.street_sab')}
                              </div>
                            )}
                            {podpis && (
                              <div className="text-[10px] text-amber-800 mt-0.5">
                                {t('rep.modal.packet.consequence', { text: podpis.posledstvie })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-[10px] text-amber-800">
                      {t('rep.modal.packet.canon_no_options')}
                    </div>
                  )}

                  <div className="text-[10px] text-amber-900 font-medium">
                    {t('rep.modal.packet.decision_disclaimer')}
                  </div>
                </div>
              );
            }

            // ── Канон не даёт значения: остановиться и спросить ──────────────
            return (
              <div key={kljuch} className="border border-slate-300 bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-800 font-medium">
                  <HelpCircle className="w-3 h-3" />
                  {t('rep.modal.packet.unknown_title')}
                </div>
                <div className="text-[11px] text-slate-700 font-mono break-all">{p.issue}</div>
                <div className="text-[10px] text-slate-600">
                  {t('rep.modal.packet.unknown_disclaimer')}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* ── Вне пакета, с названной причиной ─────────────────────────────────── */}
      {vnePaketa.length > 0 && (
        <div className="border border-sky-200 bg-sky-50/70 rounded-xl p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] text-sky-900 font-medium">
            <Clock className="w-3 h-3" />
            {t('rep.modal.packet.outside_title')}
          </div>
          {vnePaketa.map(({ dir, punkty }) => (
            <div key={dir.key} className="text-[11px] text-sky-900">
              {dir.name}: {punkty.map((p) => opisatZamechanie(p.issue, t)).join('; ')}
              <span className="text-[10px] text-sky-700">
                {t('rep.modal.packet.await_disclaimer')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitationsFixPacket;
